import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import CheckIcon from '@mui/icons-material/Check';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import useStore from '../store/useStore';
import useIsMobile from '../hooks/useIsMobile';

// Ready-made material sets from the school's books (assessment templates +
// video library, see GET /api/materials/book-presets). The teacher ticks units,
// optionally unticks single items, and gets them back in the group content
// shape { type, text, url, note } through onAdd.
//
// props: open, onClose, existingItems (current group content, used to mark
// «уже есть»), onAdd(items), onApplyDetails?({ book, level, themeProgress }).

// Same accents as the material chips in the group panel.
const SECTIONS = [
  { type: 'word', ru: 'Слова', en: 'Words', color: '#C2233E', min: 150 },
  { type: 'phrase', ru: 'Фразы', en: 'Phrases', color: '#0891b2', min: 230 },
  { type: 'video', ru: 'Видео', en: 'Videos', color: '#dc2626', min: 250 },
];
const KNOWN_TYPES = new Set(SECTIONS.map((s) => s.type));
const COUNT_FORMS = {
  word: { ru: ['слово', 'слова', 'слов'], en: ['word', 'words'] },
  phrase: { ru: ['фраза', 'фразы', 'фраз'], en: ['phrase', 'phrases'] },
  video: { ru: ['видео', 'видео', 'видео'], en: ['video', 'videos'] },
};
const NO_ITEMS = [];

// Presets change rarely: the last fetched copy shows instantly on the next
// open while a fresh one loads in the background.
let presetsCache = null;

export function pluralRu(n, [one, few, many]) {
  const mod100 = Math.abs(n) % 100;
  const mod10 = mod100 % 10;
  if (mod100 > 10 && mod100 < 20) return many;
  if (mod10 === 1) return one;
  if (mod10 > 1 && mod10 < 5) return few;
  return many;
}

function countsLabel(counts, isEn) {
  return SECTIONS.filter((s) => counts[s.type] > 0)
    .map((s) => {
      const n = counts[s.type];
      const forms = COUNT_FORMS[s.type];
      return `${n} ${isEn ? forms.en[n === 1 ? 0 : 1] : pluralRu(n, forms.ru)}`;
    })
    .join(' · ');
}

const normType = (type) => (type === 'youtube' ? 'video' : type);
const fold = (s) =>
  String(s || '')
    .toLowerCase()
    .replace(/ё/g, 'е');

// Duplicate check used for «уже есть»: same type + text (case-insensitive)
// or the same URL.
function createSeen(items) {
  const keys = new Set();
  const urls = new Set();
  const textKey = (it) => {
    const text = String(it.text || '')
      .trim()
      .toLowerCase();
    return text ? `${normType(it.type)}|${text}` : '';
  };
  const add = (it) => {
    const key = textKey(it);
    if (key) keys.add(key);
    const url = String(it.url || '').trim();
    if (url) urls.add(url);
  };
  const has = (it) => {
    const key = textKey(it);
    const url = String(it.url || '').trim();
    return (key && keys.has(key)) || (url && urls.has(url));
  };
  (items || []).forEach(add);
  return { add, has };
}

// "Unit 1 - Colors and Feelings" → "Unit 1"
const shortUnit = (name) => String(name).split(/\s+[-–—:]\s+/)[0];

function normaliseBooks(presets) {
  const list = Array.isArray(presets) ? presets : [];
  const bookOf = (p) => String(p.book || p.templateName || '').trim();
  const sameName = {};
  list.forEach((p) => {
    sameName[bookOf(p)] = (sameName[bookOf(p)] || 0) + 1;
  });
  return list.map((p, bi) => {
    const id = String(p.templateId || `book-${bi}`);
    const book = bookOf(p);
    const units = (Array.isArray(p.units) ? p.units : []).map((u, ui) => {
      const unitKey = `${id}::${ui}`;
      const name = String(u.name || '').trim() || `Unit ${ui + 1}`;
      const items = (Array.isArray(u.items) ? u.items : [])
        .map((it, ii) => ({
          key: `${unitKey}::${ii}`,
          unitKey,
          unitLabel: shortUnit(name),
          type: normType(it.type),
          text: String(it.text || '').trim(),
          url: String(it.url || '').trim(),
          note: String(it.note || '').trim(),
        }))
        .filter((it) => KNOWN_TYPES.has(it.type) && (it.text || it.url));
      const counts = { word: 0, phrase: 0, video: 0 };
      items.forEach((it) => {
        counts[it.type] += 1;
      });
      return {
        key: unitKey,
        name,
        description: String(u.description || '').trim(),
        items,
        counts,
      };
    });
    return {
      id,
      book,
      templateName: String(p.templateName || ''),
      // Two templates of the same book (e.g. two school years) need the
      // full template name to be told apart.
      label: sameName[book] > 1 ? p.templateName || book : book,
      units,
    };
  });
}

function Centered({ children }) {
  return (
    <Box
      sx={{
        flex: 1,
        minHeight: '100%',
        display: 'grid',
        placeItems: 'center',
        textAlign: 'center',
        p: 3,
      }}
    >
      {children}
    </Box>
  );
}

export default function BookMaterialsPicker({
  open,
  onClose,
  existingItems = NO_ITEMS,
  onAdd,
  onApplyDetails,
}) {
  const { language } = useStore();
  const isEn = language === 'en';
  const isMobile = useIsMobile();

  const [presets, setPresets] = useState(() => presetsCache);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [bookId, setBookId] = useState('');
  const [query, setQuery] = useState('');
  // openUnits: units shown on the right; picked: item keys that will be added
  const [openUnits, setOpenUnits] = useState(() => new Set());
  const [picked, setPicked] = useState(() => new Set());
  const [busy, setBusy] = useState('');
  const [appliedBookId, setAppliedBookId] = useState('');
  const [actionError, setActionError] = useState('');

  useEffect(() => {
    if (!open) return undefined;
    setBookId('');
    setQuery('');
    setOpenUnits(new Set());
    setPicked(new Set());
    setAppliedBookId('');
    setActionError('');
    setLoadError(false);
    setLoading(!presetsCache);
    let cancelled = false;
    fetch('/api/materials/book-presets', { credentials: 'include' })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((data) => {
        const list = Array.isArray(data) ? data : [];
        presetsCache = list;
        if (!cancelled) setPresets(list);
      })
      .catch(() => {
        if (!cancelled && !presetsCache) setLoadError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, reloadKey]);

  const books = useMemo(() => normaliseBooks(presets), [presets]);
  const seen = useMemo(() => createSeen(existingItems), [existingItems]);

  // Until the teacher picks a tab, open the book the group already uses most.
  const defaultBookId = useMemo(() => {
    let best = books[0];
    let bestHits = 0;
    books.forEach((b) => {
      let hits = 0;
      b.units.forEach((u) =>
        u.items.forEach((it) => {
          if (seen.has(it)) hits += 1;
        })
      );
      if (hits > bestHits) {
        best = b;
        bestHits = hits;
      }
    });
    return best ? best.id : '';
  }, [books, seen]);
  const book =
    books.find((b) => b.id === bookId) ||
    books.find((b) => b.id === defaultBookId) ||
    null;

  const q = fold(query.trim());
  const results = useMemo(() => {
    if (!q || !book) return null;
    const hits = [];
    book.units.forEach((u) =>
      u.items.forEach((it) => {
        if (fold(it.text).includes(q) || fold(it.note).includes(q)) {
          hits.push(it);
        }
      })
    );
    return hits;
  }, [q, book]);
  const hitsByUnit = useMemo(() => {
    if (!results) return null;
    const m = {};
    results.forEach((it) => {
      m[it.unitKey] = (m[it.unitKey] || 0) + 1;
    });
    return m;
  }, [results]);

  // Items to hand over, in book order, without duplicates (the same word in
  // two picked units is added once).
  const selection = useMemo(() => {
    const items = [];
    const counts = { word: 0, phrase: 0, video: 0 };
    let otherBooks = 0;
    if (!picked.size) return { items, counts, otherBooks };
    const taken = createSeen(existingItems);
    books.forEach((b) =>
      b.units.forEach((u) =>
        u.items.forEach((it) => {
          if (!picked.has(it.key) || taken.has(it)) return;
          taken.add(it);
          counts[it.type] += 1;
          if (book && b.id !== book.id) otherBooks += 1;
          items.push({
            type: it.type,
            text: it.text,
            url: it.url,
            note: it.note,
          });
        })
      )
    );
    return { items, counts, otherBooks };
  }, [picked, books, existingItems, book]);

  const total = selection.items.length;

  const toggleUnit = (unit) => {
    const wasOpen = openUnits.has(unit.key);
    setOpenUnits((prev) => {
      const next = new Set(prev);
      if (wasOpen) next.delete(unit.key);
      else next.add(unit.key);
      return next;
    });
    setPicked((prev) => {
      const next = new Set(prev);
      unit.items.forEach((it) => {
        if (wasOpen) next.delete(it.key);
        else if (!seen.has(it)) next.add(it.key);
      });
      return next;
    });
  };

  // Ticking an item found by search also shows its unit, so nothing picked
  // stays out of sight once the search is cleared.
  const setMany = (items, on) => {
    setPicked((prev) => {
      const next = new Set(prev);
      items.forEach((it) => {
        if (!on) next.delete(it.key);
        else if (!seen.has(it)) next.add(it.key);
      });
      return next;
    });
    if (!on) return;
    setOpenUnits((prev) => {
      const missing = items.filter((it) => !prev.has(it.unitKey));
      if (!missing.length) return prev;
      const next = new Set(prev);
      missing.forEach((it) => next.add(it.unitKey));
      return next;
    });
  };

  const handleAdd = async () => {
    if (!total || busy) return;
    setBusy('add');
    setActionError('');
    try {
      await onAdd(selection.items);
      onClose();
    } catch {
      setActionError(
        isEn
          ? 'Could not save. Please try again.'
          : 'Не удалось сохранить. Попробуйте ещё раз.'
      );
    } finally {
      setBusy('');
    }
  };

  const handleApply = async () => {
    if (!book || !onApplyDetails || busy) return;
    setBusy('apply');
    setActionError('');
    try {
      await onApplyDetails({
        book: book.book,
        level: '',
        themeProgress: book.units.map((u) => ({
          theme: u.name,
          lessonsCompleted: 0,
        })),
      });
      setAppliedBookId(book.id);
    } catch {
      setActionError(
        isEn
          ? 'Could not set the book. Please try again.'
          : 'Не удалось установить книгу. Попробуйте ещё раз.'
      );
    } finally {
      setBusy('');
    }
  };

  const renderItem = (it, color, withUnit) => {
    const exists = seen.has(it);
    const id = `bmp-${it.key}`;
    const meta = [it.note, withUnit ? it.unitLabel : '']
      .filter(Boolean)
      .join(' · ');
    return (
      <Box
        key={it.key}
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          borderRadius: 1.5,
          opacity: exists ? 0.55 : 1,
          transition: 'background-color 0.15s',
          '&:hover': exists ? undefined : { bgcolor: 'action.hover' },
        }}
      >
        <Box
          component="label"
          htmlFor={id}
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            flex: 1,
            minWidth: 0,
            py: 0.25,
            cursor: exists ? 'default' : 'pointer',
          }}
        >
          <Checkbox
            id={id}
            size="small"
            checked={!exists && picked.has(it.key)}
            disabled={exists}
            onChange={() => setMany([it], !picked.has(it.key))}
            sx={{ p: 0.5, mr: 0.5 }}
          />
          <Box sx={{ minWidth: 0, pt: 0.5, pr: 0.5 }}>
            <Typography
              variant="body2"
              sx={{ fontWeight: 600, lineHeight: 1.3, wordBreak: 'break-word' }}
            >
              {it.text || it.url}
              {exists && (
                <Box
                  component="span"
                  sx={{
                    ml: 0.75,
                    px: 0.75,
                    borderRadius: 1,
                    fontSize: '0.68rem',
                    fontWeight: 700,
                    whiteSpace: 'nowrap',
                    bgcolor: 'action.selected',
                    color: 'text.secondary',
                  }}
                >
                  {isEn ? 'already added' : 'уже есть'}
                </Box>
              )}
            </Typography>
            {meta && (
              <Typography
                variant="caption"
                color="text.secondary"
                component="div"
                sx={{ lineHeight: 1.3 }}
              >
                {meta}
              </Typography>
            )}
          </Box>
        </Box>
        {it.url && (
          <Tooltip title={isEn ? 'Open video' : 'Открыть видео'}>
            <IconButton
              size="small"
              component="a"
              href={it.url}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`${isEn ? 'Open video' : 'Открыть видео'}: ${it.text}`}
              sx={{ mt: 0.25, color }}
            >
              <PlayCircleOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    );
  };

  const renderSections = (items, scopeKey, withUnit) =>
    SECTIONS.map((s) => {
      const list = items.filter((it) => it.type === s.type);
      if (!list.length) return null;
      const avail = list.filter((it) => !seen.has(it));
      const chosen = avail.filter((it) => picked.has(it.key)).length;
      const all = avail.length > 0 && chosen === avail.length;
      const headId = `bmp-${scopeKey}-${s.type}`;
      return (
        <Box key={s.type} sx={{ mt: 1.25 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Checkbox
              size="small"
              checked={all}
              indeterminate={chosen > 0 && !all}
              disabled={!avail.length}
              onChange={() => setMany(avail, !all)}
              inputProps={{ 'aria-labelledby': headId }}
              sx={{ p: 0.5 }}
            />
            <Typography
              id={headId}
              variant="caption"
              sx={{
                fontWeight: 800,
                letterSpacing: 0.6,
                textTransform: 'uppercase',
                color: s.color,
              }}
            >
              {isEn ? s.en : s.ru}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {chosen}/{list.length}
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: `repeat(auto-fill, minmax(min(100%, ${s.min}px), 1fr))`,
              columnGap: 1,
            }}
          >
            {list.map((it) => renderItem(it, s.color, withUnit))}
          </Box>
        </Box>
      );
    });

  const renderUnitRow = (u) => {
    const isOpen = openUnits.has(u.key);
    const avail = u.items.filter((it) => !seen.has(it)).length;
    const chosen = u.items.filter((it) => picked.has(it.key)).length;
    const partial = isOpen && chosen < avail;
    const hits = hitsByUnit ? hitsByUnit[u.key] || 0 : null;
    const labelId = `bmp-unit-${u.key}`;
    return (
      <ListItemButton
        key={u.key}
        selected={isOpen}
        onClick={() => toggleUnit(u)}
        sx={{
          borderRadius: 2,
          mb: 0.25,
          py: 0.75,
          pl: 1,
          alignItems: 'flex-start',
          opacity: hits === 0 ? 0.5 : 1,
        }}
      >
        <Checkbox
          edge="start"
          size="small"
          checked={isOpen}
          indeterminate={partial}
          tabIndex={-1}
          disableRipple
          inputProps={{ 'aria-labelledby': labelId }}
          sx={{ p: 0.5, ml: 0, mr: 1 }}
        />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            id={labelId}
            variant="body2"
            noWrap
            title={u.name}
            sx={{ fontWeight: 700, lineHeight: 1.35 }}
          >
            {u.name}
          </Typography>
          {u.description && (
            <Typography
              variant="caption"
              color="text.secondary"
              component="div"
              noWrap
              title={u.description}
            >
              {u.description}
            </Typography>
          )}
          <Typography
            variant="caption"
            component="div"
            sx={{ color: 'text.secondary', opacity: 0.8 }}
          >
            {countsLabel(u.counts, isEn) || (isEn ? 'empty' : 'пусто')}
            {u.items.length > 0 &&
              !avail &&
              (isEn ? ' · all added' : ' · всё уже есть')}
          </Typography>
        </Box>
        {hits ? (
          <Box
            component="span"
            sx={{
              ml: 1,
              mt: 0.25,
              px: 0.75,
              borderRadius: 999,
              fontSize: '0.7rem',
              fontWeight: 800,
              color: 'primary.contrastText',
              bgcolor: 'primary.main',
            }}
            aria-label={isEn ? `${hits} matches` : `совпадений: ${hits}`}
          >
            {hits}
          </Box>
        ) : null}
      </ListItemButton>
    );
  };

  const renderBody = () => {
    if (!loadError && !books.length && (loading || presets === null)) {
      return (
        <Centered>
          <CircularProgress size={28} />
        </Centered>
      );
    }
    if (loadError) {
      return (
        <Centered>
          <Alert
            severity="error"
            action={
              <Button
                color="inherit"
                size="small"
                onClick={() => setReloadKey((k) => k + 1)}
              >
                {isEn ? 'Retry' : 'Повторить'}
              </Button>
            }
          >
            {isEn ? 'Could not load the books' : 'Не удалось загрузить книги'}
          </Alert>
        </Centered>
      );
    }
    if (!book) {
      return (
        <Centered>
          <Typography variant="body2" color="text.secondary">
            {isEn
              ? 'No books yet — they come from the assessment templates.'
              : 'Книг пока нет — они берутся из шаблонов оценки.'}
          </Typography>
        </Centered>
      );
    }

    const shownUnits = book.units.filter((u) => openUnits.has(u.key));
    return (
      <>
        <Box
          sx={{
            width: isMobile ? 'auto' : 300,
            maxHeight: isMobile ? '40vh' : 'none',
            flexShrink: 0,
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            borderRight: isMobile ? 0 : 1,
            borderBottom: isMobile ? 1 : 0,
            borderColor: 'divider',
          }}
        >
          <Box sx={{ p: 1.5, pb: 1 }}>
            <TextField
              size="small"
              fullWidth
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                // Esc clears the search first instead of closing the dialog
                if (e.key === 'Escape' && query) {
                  e.stopPropagation();
                  setQuery('');
                }
              }}
              placeholder={
                isEn ? 'Search words, phrases, videos' : 'Поиск по книге'
              }
              slotProps={{
                htmlInput: {
                  'aria-label': isEn ? 'Search the book' : 'Поиск по книге',
                },
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                  endAdornment: query ? (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        edge="end"
                        onClick={() => setQuery('')}
                        aria-label={isEn ? 'Clear search' : 'Очистить поиск'}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                },
              }}
            />
          </Box>
          <List
            dense
            disablePadding
            aria-label={isEn ? 'Units' : 'Юниты'}
            sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: 1, pb: 1 }}
          >
            {book.units.map(renderUnitRow)}
          </List>
        </Box>

        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            minHeight: 0,
            overflowY: 'auto',
            px: { xs: 1.5, sm: 2.5 },
            py: 2,
          }}
        >
          {results ? (
            <>
              <Typography variant="caption" color="text.secondary">
                {isEn
                  ? `Found ${results.length} in ${book.label}`
                  : `Найдено ${results.length} в «${book.label}»`}
              </Typography>
              {results.length ? (
                renderSections(results, 'search', true)
              ) : (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 2 }}
                >
                  {isEn ? 'Nothing found' : 'Ничего не найдено'}
                </Typography>
              )}
            </>
          ) : shownUnits.length ? (
            shownUnits.map((u) => (
              <Box
                component="section"
                key={u.key}
                aria-label={u.name}
                sx={{ mb: 3, '&:last-of-type': { mb: 0 } }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1,
                    pb: 0.75,
                    borderBottom: 1,
                    borderColor: 'divider',
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                      {u.name}
                    </Typography>
                    {u.description && (
                      <Typography variant="caption" color="text.secondary">
                        {u.description}
                      </Typography>
                    )}
                  </Box>
                  <Tooltip title={isEn ? 'Remove unit' : 'Убрать юнит'}>
                    <IconButton
                      size="small"
                      onClick={() => toggleUnit(u)}
                      aria-label={`${isEn ? 'Remove' : 'Убрать'} ${u.name}`}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
                {u.items.length ? (
                  renderSections(u.items, u.key, false)
                ) : (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 1 }}
                  >
                    {isEn
                      ? 'No materials in this unit yet'
                      : 'В этом юните пока нет материалов'}
                  </Typography>
                )}
              </Box>
            ))
          ) : (
            <Centered>
              <Box sx={{ color: 'text.secondary', maxWidth: 340 }}>
                <MenuBookIcon sx={{ fontSize: 40, opacity: 0.35 }} />
                <Typography
                  variant="body2"
                  sx={{ mt: 1, fontWeight: 700, color: 'text.primary' }}
                >
                  {isEn
                    ? `Tick one or more units ${isMobile ? 'above' : 'on the left'}`
                    : `Отметьте один или несколько юнитов ${isMobile ? 'выше' : 'слева'}`}
                </Typography>
                <Typography variant="caption" component="div">
                  {isEn
                    ? 'Several at once work too — e.g. Unit 1 + Unit 2 for revision'
                    : 'Можно сразу несколько — например, Unit 1 и Unit 2 для повторения'}
                </Typography>
              </Box>
            </Centered>
          )}
        </Box>
      </>
    );
  };

  const applied = Boolean(book) && appliedBookId === book.id;

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      fullScreen={isMobile}
      fullWidth
      maxWidth="lg"
      aria-labelledby="book-picker-title"
      onKeyDown={(e) => {
        // Ctrl/Cmd+Enter adds the selection from anywhere in the dialog
        if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
          e.preventDefault();
          handleAdd();
        }
      }}
      slotProps={{
        paper: { sx: { height: isMobile ? '100%' : 'min(780px, 90vh)' } },
      }}
    >
      <DialogTitle
        id="book-picker-title"
        component="div"
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
          py: 1.5,
          pr: 1,
        }}
      >
        <Box
          sx={{
            width: 36,
            height: 36,
            flexShrink: 0,
            borderRadius: 2,
            display: 'grid',
            placeItems: 'center',
            color: 'primary.main',
            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
          }}
        >
          <MenuBookIcon fontSize="small" />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            component="h2"
            variant="subtitle1"
            sx={{ fontWeight: 800, lineHeight: 1.2 }}
          >
            {isEn ? 'Materials from the book' : 'Материалы из книги'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {isEn
              ? 'Pick units — their words, phrases and videos get ticked'
              : 'Выберите юниты — их слова, фразы и видео отметятся сами'}
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          disabled={Boolean(busy)}
          aria-label={isEn ? 'Close' : 'Закрыть'}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {books.length > 0 && !loadError && (
        <Tabs
          value={book ? book.id : false}
          onChange={(_, v) => setBookId(v)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          aria-label={isEn ? 'Books' : 'Книги'}
          sx={{
            px: 1,
            minHeight: 40,
            borderBottom: 1,
            borderColor: 'divider',
            '& .MuiTab-root': { minHeight: 40, py: 0.5, fontWeight: 700 },
          }}
        >
          {books.map((b) => (
            <Tab
              key={b.id}
              value={b.id}
              label={b.label || (isEn ? 'Untitled' : 'Без названия')}
              title={b.templateName}
            />
          ))}
        </Tabs>
      )}

      <DialogContent
        sx={{
          p: 0,
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          overflow: 'hidden',
          minHeight: 0,
        }}
      >
        {renderBody()}
      </DialogContent>

      <DialogActions
        disableSpacing
        sx={{
          px: { xs: 1.5, sm: 2.5 },
          py: 1.25,
          gap: 1,
          flexWrap: 'wrap',
          borderTop: 1,
          borderColor: 'divider',
        }}
      >
        <Box sx={{ flex: '1 1 220px', minWidth: 0 }}>
          {actionError ? (
            <Typography variant="caption" color="error" role="alert">
              {actionError}
            </Typography>
          ) : (
            <Typography variant="caption" color="text.secondary">
              {total
                ? `${isEn ? 'Selected' : 'Выбрано'}: ${countsLabel(selection.counts, isEn)}`
                : isEn
                  ? 'Nothing selected yet'
                  : 'Пока ничего не выбрано'}
              {selection.otherBooks > 0 &&
                (isEn
                  ? ` (incl. ${selection.otherBooks} from other books)`
                  : ` (вкл. ${selection.otherBooks} из других книг)`)}
            </Typography>
          )}
        </Box>
        {!isMobile && (
          <Button onClick={onClose} disabled={Boolean(busy)}>
            {isEn ? 'Cancel' : 'Отмена'}
          </Button>
        )}
        {onApplyDetails && (
          <Tooltip
            title={
              book
                ? isEn
                  ? `Set «${book.book}» as the group's book and its ${book.units.length} units as progress themes`
                  : `«${book.book}» станет учебником группы, ${book.units.length} ${pluralRu(book.units.length, ['юнит', 'юнита', 'юнитов'])} — темами прогресса`
                : ''
            }
          >
            <Box
              component="span"
              sx={{
                display: 'inline-flex',
                flex: isMobile ? '1 1 auto' : '0 0 auto',
              }}
            >
              <Button
                variant="outlined"
                fullWidth={isMobile}
                onClick={handleApply}
                disabled={!book || !book.units.length || Boolean(busy)}
                startIcon={
                  busy === 'apply' ? (
                    <CircularProgress size={14} color="inherit" />
                  ) : applied ? (
                    <CheckIcon />
                  ) : null
                }
              >
                {applied
                  ? isEn
                    ? 'Book set'
                    : 'Книга установлена'
                  : isEn
                    ? 'Set book & units'
                    : 'Установить книгу и юниты'}
              </Button>
            </Box>
          </Tooltip>
        )}
        <Button
          variant="contained"
          sx={{ flex: isMobile ? '1 1 auto' : 'none' }}
          onClick={handleAdd}
          disabled={!total || Boolean(busy)}
          startIcon={
            busy === 'add' ? (
              <CircularProgress size={14} color="inherit" />
            ) : null
          }
        >
          {isEn ? `Add (${total})` : `Добавить (${total})`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
